/**
 * POCKET DOORS CARVE THEIR POCKET (2026-10-07, Matt: pocketing multi-slides "have a
 * frame that would preclude them from sliding into the pocket … don't carve out their
 * pocket in the wall they are hosted to and they require a special middle wall to be
 * hosted in the first place … they don't animate correctly — instead of retracting into
 * their pocket, they stack"; and single pocket doors "don't actually cut out their
 * framing and show in the floor plan as having a pocket").
 *
 * How a real pocket door is built (the research this rule follows):
 *  - The rough opening is TWICE the door width plus about an inch — the door's own
 *    opening plus a POCKET of the same length beside it. One header spans the whole RO;
 *    there are no full studs in the pocket, only split studs, and the bottom plate is
 *    cut out across it.
 *  - The pocket is a CAVITY inside the wall: the leaf rides an overhead track between
 *    two thin skins (split stud + drywall), with about 1/2" of clearance each side.
 *  - The jamb on the pocket side is a SPLIT JAMB — two thin strips, one on each face,
 *    with a slot between them the leaf passes through. Only the strike side has a full
 *    jamb. Hardware is a flush edge pull, never a lever (it would hit the skins).
 *  - Open, the leaf retracts until its lead edge is flush with the split jamb.
 *  - A multi-panel pocket (stacking glass doors that disappear into the wall) parks the
 *    whole stack, one panel per track, inside a pocket one panel long. The stack is
 *    deeper than a stud wall, so the wall at the pocket is FURRED OUT — thickened on the
 *    room side, the exterior face kept flush.
 *
 * ONE rule, read by the wall generator (the cut, the furring, the framing RO), the plan
 * (where the door sits across the wall) and the door prototype (split jamb, depth, the
 * retraction motion) — so the three can never disagree.
 *
 * EXTERIOR POCKETS PARK BEHIND THE WALL (2026-10-07, Matt: "An exterior pocketing door
 * needs to sit behind the exterior wall framing and then have an interior wall for its
 * interior pocket. We typically model this as two walls with a gap between them. We
 * don't need to cut a pocket into the hosting wall's studs. That would compromise the
 * outer structure and insulation."): a pocket door hosted in an EXTERIOR wall never cuts
 * or furs that wall — only its opening goes through (a normal header over the opening
 * alone). The panels ride a track in the GAP between the exterior wall and the modeled
 * room-side wall (`findPocketRoom` — parallel walls on the interior side), centered in
 * it, and park there. The door cuts its opening through the room-side wall too (a
 * guest copy, the seam-guest channel). No room-side wall = a standard gap. A gap too
 * narrow for the stack is flagged (`tight`) and drawn so it reads. The cut-into-studs
 * pocket below stays the rule for pocket doors in INTERIOR partitions, where a pocket
 * frame really does replace the studs.
 */
import type { ParamValue, Path2D, Pt2, ResolvedElement } from '@aj/bim-kernel'
import { resolveOpeningFrame, wallFaceOffsets, centerlineOffsetFt, flattenPathPts, DEFAULT_ARC_TOL_FT, isCantedWallParams } from '@aj/bim-kernel'
import { slideLanes } from './slideLanes'
import { wallFrame, placeOpening, registerPocketRoomFinder } from '../../openings'

/** door leaf thickness (protos.ts LEAF_T — duplicated to keep this module dependency-light) */
const LEAF_T = 0.146
/** multi-slide track pitch (protos.ts: LEAF_T * 1.05) */
const TRACK_STEP = LEAF_T * 1.05
/** meeting-stile overlap (protos.ts SASH_OVL) */
const SASH_OVL = 0.12
/** clearance between the parked leaf/stack and each skin */
export const POCKET_CLEAR_FT = 0.5 / 12
/** thinnest wall skin either side of the cavity — a pocket frame's steel-wrapped split
 *  stud is thin enough that a standard 1-3/4" pocket door fits a finished 2x4 wall
 *  (4-1/2") with no furring; a stack deeper than that furs out */
export const POCKET_MIN_SKIN_FT = 0.625 / 12
/** extra pocket length past the parked leaf (bumper + stop) */
const POCKET_TAIL_FT = 1 / 12
/** king stud beyond the pocket's end — the furred housing runs past it */
const HOUSING_END_FT = 2 / 12

export function isPocketDoor(p: Record<string, ParamValue>): boolean {
  const op = String(p.operation || '')
  return op === 'pocket' || (op === 'multiSlide' && p.parkMode === 'pocket')
}

/** depth of the parked leaf / stack (outer face to outer face) */
export function pocketStackDepthFt(p: Record<string, ParamValue>, direction: string): number {
  if (String(p.operation) !== 'multiSlide') return LEAF_T
  const N = Math.max(2, Math.min(8, Math.round((p.panelCount as number) || 3)))
  const { count } = slideLanes(N, direction)
  return (count - 1) * TRACK_STEP + LEAF_T
}

/** the cavity's own thickness — the stack plus clearance either side */
export function pocketCavityFt(p: Record<string, ParamValue>, direction: string): number {
  return pocketStackDepthFt(p, direction) + 2 * POCKET_CLEAR_FT
}

export interface PocketSide {
  /** −1 = the pocket runs past the opening's s0 jamb, +1 = past s1 */
  dir: -1 | 1
  /** pocket length measured from the rough-opening edge (s0/s1) outward */
  lenFt: number
}

export interface PocketLayout {
  /** across-wall offset (from the wall CENTERLINE, along its left normal) of the
   *  door's track center — where the door sits (plan symbol + 3D instance) */
  c: number
  /** the cavity across the wall, [lo, hi] from the centerline */
  cavLo: number
  cavHi: number
  /** the wall body across the opening: the wall, or the wall + its furred housing */
  bodyLo: number
  bodyHi: number
  /** true when the wall is thickened (furred) to house the pocket */
  furred: boolean
  sides: PocketSide[]
  /** the furred housing's extent past each pocket end (king stud) */
  housingEndFt: number
  /** EXTERIOR POCKETS: the door parks in the gap behind its exterior host — nothing is
   *  cut or furred; [cavLo, cavHi] = [bodyLo, bodyHi] = the gap */
  behind?: boolean
  /** the gap is narrower than the panel stack needs */
  tight?: boolean
}

/** where each pocket runs and how long it is: the parked leaf/stack's lead edge stops
 *  flush with the split jamb's inner face (i0/i1), so the pocket holds one leaf (or one
 *  panel) less the jamb width, plus a little tail */
function pocketSides(p: Record<string, ParamValue>): PocketSide[] {
  const op = String(p.operation)
  const width = (p.widthFt as number) || 3
  const fw = resolveOpeningFrame('door', p).widthFt
  const dirP = String(p.direction || 'L')
  if (op === 'pocket') return [{ dir: p.hand === 'R' ? 1 : -1, lenFt: width - fw + POCKET_TAIL_FT }]
  const N = Math.max(2, Math.min(8, Math.round((p.panelCount as number) || 3)))
  const len = width / N + SASH_OVL - fw + POCKET_TAIL_FT
  if (dirP === 'split') return [{ dir: -1, lenFt: len }, { dir: 1, lenFt: len }]
  return [{ dir: dirP === 'R' ? 1 : -1, lenFt: len }]
}

type HostLike = {
  assemblyThicknessFt: number; params: Record<string, ParamValue>
  assembly?: ResolvedElement['assembly']; typeParams?: Record<string, ParamValue>
}

/**
 * The pocket a door makes in its host wall. Null = not a pocketing door. `hostT` /
 * `flipped` / `assembly` / `hostFunction` describe the host wall.
 */
export function pocketLayout(
  opening: Pick<ResolvedElement, 'category' | 'typeParams' | 'params'>,
  host: HostLike,
): PocketLayout | null {
  if (opening.category !== 'door') return null
  const p = { ...opening.typeParams, ...opening.params } as Record<string, ParamValue>
  if (!isPocketDoor(p)) return null
  const t = host.assemblyThicknessFt
  if (!(t > 0)) return null
  const dirP = String(p.direction || 'L')
  const sides = pocketSides(p)
  const cav = pocketCavityFt(p, dirP)
  const half = t / 2
  if (host.typeParams?.function === 'exterior') {
    // EXTERIOR POCKETS PARK BEHIND THE WALL: the gap to the room-side wall (stamped by
    // whoever found it — `__pocketGap`), else a standard gap just wide enough
    const stamped = p.__pocketGap
    const gap = typeof stamped === 'number' && stamped > 0 ? stamped : cav
    const { exteriorOff, interiorOff } = wallFaceOffsets(host.params.flipped, host.assembly)
    const sI = Math.sign(interiorOff - exteriorOff) || -1
    const c = interiorOff + sI * gap / 2
    return {
      c, cavLo: c - gap / 2, cavHi: c + gap / 2, bodyLo: c - gap / 2, bodyHi: c + gap / 2,
      furred: false, sides, housingEndFt: 0, behind: true,
      tight: gap < pocketStackDepthFt(p, dirP) + 0.25 / 12 - 1e-9,
    }
  }
  const need = cav + 2 * POCKET_MIN_SKIN_FT
  if (need <= t + 1e-9) {
    // fits inside the wall — the cavity centered, skins either side
    return { c: 0, cavLo: -cav / 2, cavHi: cav / 2, bodyLo: -half, bodyHi: half, furred: false, sides, housingEndFt: 0 }
  }
  // too deep for the wall: FUR it out. An exterior wall keeps its exterior face flush
  // (siding runs straight past) and grows toward the room; an interior wall grows evenly.
  let c = 0
  if (host.typeParams?.function === 'exterior') {
    const { exteriorOff } = wallFaceOffsets(host.params.flipped, host.assembly)
    const sE = Math.sign(exteriorOff) || 1
    c = exteriorOff - sE * need / 2
  }
  return {
    c,
    cavLo: c - cav / 2, cavHi: c + cav / 2,
    bodyLo: Math.min(-half, c - need / 2), bodyHi: Math.max(half, c + need / 2),
    furred: true,
    sides,
    housingEndFt: HOUSING_END_FT,
  }
}

/** the pocket's extent along the wall for an opening spanning [s0, s1] — the full
 *  rough opening the framing sees (one header over door + pocket(s)) */
export function pocketRoughOpening(layout: PocketLayout, s0: number, s1: number): { s0: number; s1: number } {
  let a = s0, b = s1
  for (const sd of layout.sides) {
    if (sd.dir < 0) a = s0 - sd.lenFt
    else b = s1 + sd.lenFt
  }
  return { s0: a, s1: b }
}

export interface PocketRoom {
  /** the clear gap between the exterior wall's room face and the room-side wall */
  gapFt: number
  /** every room-side wall the pocket depends on (the regeneration edge) */
  walls: string[]
  /** the room-side walls the OPENING crosses — each gets the opening cut through it,
   *  as the opening's span mapped into that wall's own stations */
  guests: { wallId: string; s0: number; s1: number }[]
}

/**
 * EXTERIOR POCKETS PARK BEHIND THE WALL: the room-side wall(s) of an exterior pocket —
 * walls on the host's level, parallel to it, on its INTERIOR side, no more than 2-1/2'
 * off its room face, running along the door + pocket. Null = not an exterior pocket
 * door, or nothing modeled there (the door then takes a standard gap).
 */
export function findPocketRoom(
  opening: ResolvedElement, host: ResolvedElement | null | undefined, walls: Iterable<ResolvedElement>,
): PocketRoom | null {
  if (!host || opening.category !== 'door' || host.typeParams?.function !== 'exterior') return null
  const p = { ...opening.typeParams, ...opening.params } as Record<string, ParamValue>
  if (!isPocketDoor(p)) return null
  const path = host.params.path as Path2D | undefined
  if (!path?.nodes || path.nodes.length < 2) return null
  const pts = flattenPathPts(path, DEFAULT_ARC_TOL_FT)
  if (pts.length < 2) return null
  const frame = wallFrame(pts)
  const pl = placeOpening(opening, frame, 0)
  if (!pl) return null
  let span0 = pl.s0, span1 = pl.s1
  for (const sd of pocketSides(p)) {
    if (sd.dir < 0) span0 = pl.s0 - sd.lenFt
    else span1 = pl.s1 + sd.lenFt
  }
  const at = frame.pointAt(pl.center)
  const dir = at.dir
  const n: Pt2 = [-dir[1], dir[0]]
  const delta = centerlineOffsetFt(host.params.locationLine, host.params.flipped, host.assembly)
  const { exteriorOff, interiorOff } = wallFaceOffsets(host.params.flipped, host.assembly)
  const sI = Math.sign(interiorOff - exteriorOff) || -1
  const across = (q: Pt2) => (q[0] - at.pt[0]) * n[0] + (q[1] - at.pt[1]) * n[1] - delta
  const along = (q: Pt2) => pl.center + (q[0] - at.pt[0]) * dir[0] + (q[1] - at.pt[1]) * dir[1]
  let gap = Infinity
  const partners: string[] = []
  const guests: PocketRoom['guests'] = []
  for (const w of walls) {
    if (w.category !== 'wall' || w.id === host.id || w.levelId !== host.levelId) continue
    if (isCantedWallParams(w.params)) continue
    const wp = w.params.path as Path2D | undefined
    if (!wp?.nodes || wp.nodes.length < 2) continue
    const wpts = flattenPathPts(wp, DEFAULT_ARC_TOL_FT)
    const dW = centerlineOffsetFt(w.params.locationLine, w.params.flipped, w.assembly)
    const tw = w.assemblyThicknessFt || 0
    let cum = 0
    let hit = false
    for (let i = 0; i + 1 < wpts.length; i++) {
      const A = wpts[i], B = wpts[i + 1]
      const segLen = Math.hypot(B[0] - A[0], B[1] - A[1])
      const segStart = cum
      cum += segLen
      if (segLen < 1e-6) continue
      const u: Pt2 = [(B[0] - A[0]) / segLen, (B[1] - A[1]) / segLen]
      const dot = u[0] * dir[0] + u[1] * dir[1]
      if (Math.abs(dot) < 0.999) continue
      // the wall's own centerline sits dW along ITS left normal — ±n here
      const aw = across(A) + dW * Math.sign(dot)
      const gapW = sI * ((aw - sI * tw / 2) - interiorOff)
      if (gapW < 0.04 || gapW > 2.5) continue
      const sa = along(A), sb = along(B)
      const lo = Math.min(sa, sb), hi = Math.max(sa, sb)
      if (Math.min(hi, span1) - Math.max(lo, span0) < 0.1) continue
      gap = Math.min(gap, gapW)
      hit = true
      // the opening's span in this wall's own stations (straight along this segment;
      // the generator clips whatever runs past the wall's ends)
      if (Math.min(hi, pl.s1) - Math.max(lo, pl.s0) > 0.05) {
        const st = (x: number) => segStart + ((x - sa) / (sb - sa)) * segLen
        const g0 = st(pl.s0), g1 = st(pl.s1)
        guests.push({ wallId: w.id, s0: Math.min(g0, g1), s1: Math.max(g0, g1) })
      }
    }
    if (hit) partners.push(w.id)
  }
  if (!partners.length) return null
  return { gapFt: Math.round(gap * 1e5) / 1e5, walls: partners, guests }
}

registerPocketRoomFinder((o, h, w) => findPocketRoom(o, h, w))
